import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { QASkeleton } from './QASkeleton';
import { ReviewCasePanel } from './ReviewCasePanel';
import { formatAUD, formatDateAU, formatPct } from '../../../shared/format';
import type { Brief, BriefSeverity } from '../../../shared/brief';

interface BriefResponse {
  today: string;
  client: { id: string; name: string; segment: string | null; riskProfile: string | null };
  people: Array<{ name: string; role: string; dateOfBirth: string | null }>;
  financial: boolean;
  brief: Brief;
}

const SEV: Record<BriefSeverity, string> = { high: 'border-rust-deep', medium: 'border-rust', info: 'border-brass' };

/**
 * Pre-meeting brief: one printable page drawn from the record and the file
 * notes. Read-only; the agenda is a suggestion for the adviser, not advice.
 */
export function BriefTab({ clientId, canEdit = false, onOpenTab }: { clientId: string; canEdit?: boolean; onOpenTab?: (tab: string) => void }) {
  const q = useQuery({ queryKey: ['client', clientId, 'brief'], queryFn: () => api<{ data: BriefResponse }>(`/api/clients/${clientId}/brief`) });
  if (q.isLoading) return <QASkeleton variant="detail" />;
  if (q.isError || !q.data) return <div className="qa-badge qa-badge--out">Unable to load the brief</div>;
  const { client, brief: b, financial, today } = q.data.data;

  return (
    <>
    <ReviewCasePanel clientId={clientId} canEdit={canEdit} onOpenTab={onOpenTab} />
    <article className="qa-card qa-print-area p-6 md:p-8 max-w-4xl" data-testid="brief" aria-label="Pre-meeting brief">
      <header className="flex flex-wrap items-start justify-between gap-3 mb-6 pb-4 border-b border-hairline-paper">
        <div>
          <p className="qa-eyebrow mb-1">Pre-meeting brief · prepared {formatDateAU(today)}</p>
          <h2 className="qa-h2">{client.name}</h2>
          <p className="text-xs text-ink-mute mt-1">
            {[client.segment, client.riskProfile && `Risk profile: ${client.riskProfile}`, b.meetingDate && `Next due ${formatDateAU(b.meetingDate)}`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex gap-2 qa-no-print">
          <a className="btn-ghost text-xs" href={`/api/clients/${clientId}/brief/pdf`} target="_blank" rel="noopener noreferrer" data-testid="brief-pack-pdf">Review pack PDF</a>
          {canEdit && <SavePackButton clientId={clientId} />}
          <button type="button" className="btn-ghost text-xs" onClick={() => window.print()}>Print</button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <Block title="Suggested agenda" wide testId="brief-agenda">
          <ol className="list-decimal pl-5 space-y-1.5 text-sm">
            {b.agenda.map((a, i) => <li key={i}>{a}</li>)}
          </ol>
        </Block>

        <Block title={b.lastMeeting ? `Since we met on ${formatDateAU(b.lastMeeting.date)}` : 'Last 90 days'} testId="brief-since">
          {b.lastMeeting && <p className="text-xs text-ink-soft italic mb-2">Last meeting: {b.lastMeeting.excerpt}</p>}
          {b.since.value && (
            <p className="text-sm mb-2">
              Portfolio <span className="qa-mono">{formatAUD(b.since.value.to)}</span>,{' '}
              <span className={b.since.value.change >= 0 ? 'text-forest' : 'text-rust'}>
                {b.since.value.change >= 0 ? '+' : '−'}{formatAUD(Math.abs(b.since.value.change))}
                {b.since.value.pct != null && ` (${formatPct(Math.abs(b.since.value.pct))})`}
              </span>{' '}since {formatDateAU(b.since.value.fromDate)}
            </p>
          )}
          {b.since.notes.length === 0 && b.since.advice.length === 0 ? (
            <p className="text-xs text-ink-mute">Nothing recorded since.</p>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {b.since.advice.map((a, i) => <li key={`a${i}`}><span className="qa-mono text-ink-mute">{formatDateAU(a.date)}</span> {a.label}</li>)}
              {b.since.notes.map((n, i) => (
                <li key={`n${i}`}><span className="qa-mono text-ink-mute">{formatDateAU(n.date)}</span> <span className="font-medium">{n.type}:</span> {n.excerpt}</li>
              ))}
            </ul>
          )}
        </Block>

        <Block title="Open items" testId="brief-open">
          {b.open.length === 0 ? <p className="text-xs text-ink-mute">Nothing open.</p> : (
            <ul className="space-y-1.5">
              {b.open.map((o, i) => (
                <li key={i} className={`pl-2 border-l-2 ${SEV[o.severity]} text-xs`}><span className="font-semibold">{o.label}:</span> {o.detail}</li>
              ))}
            </ul>
          )}
        </Block>

        {financial && (
          <Block title="Allocation drift" testId="brief-drift">
            {b.drift.length === 0 ? <p className="text-xs text-ink-mute">Within 5% of target, or no targets set.</p> : (
              <table className="qa-table text-xs w-full">
                <thead><tr><th>Class</th><th className="text-right">Actual</th><th className="text-right">Target</th><th className="text-right">Drift</th></tr></thead>
                <tbody>
                  {b.drift.map((r) => (
                    <tr key={r.label}>
                      <td>{r.label}</td>
                      <td className="num">{formatPct(r.actual)}</td>
                      <td className="num">{formatPct(r.target ?? 0)}</td>
                      <td className={`num ${(r.drift ?? 0) > 0 ? 'text-rust' : 'text-brass-deep'}`}>{(r.drift ?? 0) > 0 ? '+' : ''}{formatPct(r.drift ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Block>
        )}

        <Block title={b.caps.length ? `Contribution caps ${b.caps[0].fy}` : 'Contribution caps'} testId="brief-caps">
          {b.caps.length === 0 ? <p className="text-xs text-ink-mute">No planned concessional contributions on the fact find.</p> : (
            <table className="qa-table text-xs w-full">
              <thead><tr><th>Member</th><th className="text-right">Cap</th><th className="text-right">Planned</th><th className="text-right">Room</th></tr></thead>
              <tbody>
                {b.caps.map((x) => (
                  <tr key={x.name}><td>{x.name}</td><td className="num">{formatAUD(x.cap)}</td><td className="num">{formatAUD(x.planned)}</td><td className="num font-semibold">{formatAUD(x.room)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-[11px] text-ink-mute mt-2">
            Non-concessional cap {formatAUD(b.nonConcessionalCap)} a year. Unused concessional cap from the last five years can carry forward when the total super balance was under $500,000 at 30 June; confirm on ATO online services.
          </p>
        </Block>

        <Block title="Coming up" testId="brief-coming">
          {b.comingUp.length === 0 ? <p className="text-xs text-ink-mute">Nothing in the next four months.</p> : (
            <ul className="space-y-1 text-xs">
              {b.comingUp.map((e, i) => <li key={i}><span className="qa-mono text-ink-mute">{formatDateAU(e.date)}</span> {e.label}</li>)}
            </ul>
          )}
        </Block>
      </div>
    </article>
    </>
  );
}

function Block({ title, children, wide, testId }: { title: string; children: React.ReactNode; wide?: boolean; testId: string }) {
  return (
    <section className={wide ? 'md:col-span-2' : ''} data-testid={testId} aria-label={title}>
      <h3 className="qa-eyebrow mb-2">{title}</h3>
      {children}
    </section>
  );
}

/** Files the review pack PDF in the vault, so the copy taken to the meeting is on record. */
function SavePackButton({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  async function save() {
    setState('busy');
    try {
      await api(`/api/clients/${clientId}/brief/pdf/save`, { method: 'POST', body: '{}' });
      setState('done');
      void qc.invalidateQueries({ queryKey: ['client', clientId, 'documents'] });
    } catch { setState('error'); }
  }
  return (
    <button type="button" className="btn-ghost text-xs" disabled={state === 'busy' || state === 'done'} onClick={save} data-testid="brief-pack-save">
      {state === 'busy' ? 'Saving...' : state === 'done' ? 'Saved to vault' : state === 'error' ? 'Save failed, retry' : 'Save pack to vault'}
    </button>
  );
}
